import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import {
  IconListDetails,
  IconPlugConnected,
  IconRefresh,
  IconUsers,
} from '@tabler/icons-react';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo, useState } from 'react';
import { AzureGroupScopeMappingDto } from 'src/api/FiretrolApiClient';
import {
  useAzureGroupSyncMappings,
  useAzureGroupSyncStatus,
} from 'src/api/queries/admin/useAzureGroupSync';
import {
  useAzureGroupSyncFullSync,
  useDeleteAzureGroupSyncMapping,
  useTestAzureGroupSyncConnection,
} from 'src/api/queries/admin/useAzureGroupSyncMutations';
import { showErrorToast, showSuccessToast, formatDateTime } from 'src/utils';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { AzureGroupSyncMappingDialog } from './components/AzureGroupSyncMappingDialog';
import { AzureGroupSyncMembershipsDrawer } from './components/AzureGroupSyncMembershipsDrawer';
import { AzureGroupSyncLogsDrawer } from './components/AzureGroupSyncLogsDrawer';

const getColumns = (): MRT_ColumnDef<AzureGroupScopeMappingDto>[] => [
  {
    accessorKey: 'scope',
    header: 'Scope',
  },
  {
    accessorKey: 'azureGroupId',
    header: 'Azure Group ID',
    Cell: ({ cell }) => (
      <Text size="xs" ff="monospace">
        {cell.getValue<string>()}
      </Text>
    ),
  },
  {
    accessorKey: 'azureGroupDisplayName',
    header: 'Display Name',
    Cell: ({ cell }) => cell.getValue<string | undefined>() || '—',
  },
  {
    accessorKey: 'isEnabled',
    header: 'Enabled',
    size: 100,
    Cell: ({ cell }) => (
      <Badge color={cell.getValue<boolean>() ? 'green' : 'gray'}>
        {cell.getValue<boolean>() ? 'Yes' : 'No'}
      </Badge>
    ),
  },
  {
    accessorKey: 'createdAt',
    header: 'Created At',
    Cell: ({ cell }) => {
      const val = cell.getValue<Date | null>();
      return val ? formatDateTime(val) : '—';
    },
  },
];

export const AzureGroupSync: React.FC = () => {
  const mappingsQuery = useAzureGroupSyncMappings();
  const statusQuery = useAzureGroupSyncStatus();
  const mappings = useMemo(
    () => mappingsQuery.data ?? [],
    [mappingsQuery.data],
  );
  const status = statusQuery.data;
  const testConnection = useTestAzureGroupSyncConnection();
  const fullSync = useAzureGroupSyncFullSync();
  const deleteMapping = useDeleteAzureGroupSyncMapping();
  const [dialogState, setDialogState] = useState<{
    isOpen: boolean;
    mapping: AzureGroupScopeMappingDto | null;
  }>({ isOpen: false, mapping: null });
  const [membershipsOpen, setMembershipsOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);

  const refresh = () => {
    mappingsQuery.refetch();
    statusQuery.refetch();
  };

  const handleTestConnection = () =>
    testConnection.mutate(undefined, {
      onSuccess: (result) =>
        result.success
          ? showSuccessToast('Azure AD connection successful!')
          : showErrorToast(`Connection failed: ${result.error}`),
    });

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'azure-group-sync-mappings',
    columns: columns,
    defaultSorting: [{ id: 'scope', desc: false }],
  });

  const { table } = useTable({
    ...tableState,
    onRefresh: refresh,
    tableOptions: {
      enableColumnFilters: false,
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 120 } },
      renderRowActions: ({ row }) => {
        const mapping = row.original;
        return (
          <Group gap="xs">
            <EditActionIcon
              onClick={() => setDialogState({ isOpen: true, mapping })}
            />
            <DeleteActionIcon
              requiredConfirmation
              confirmationText={`Delete mapping for scope "${mapping.scope}"?`}
              onDelete={() => deleteMapping.mutate(mapping.id)}
            />
          </Group>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: mappings,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton
            onClick={() => setDialogState({ isOpen: true, mapping: null })}
          >
            Add Mapping
          </CreateButton>
          <Tooltip label="Test Connection">
            <BasicActionIcon
              variant="light"
              color="blue"
              onClick={handleTestConnection}
              loading={testConnection.isPending}
              disabled={testConnection.isPending || fullSync.isPending}
            >
              <IconPlugConnected size={18} />
            </BasicActionIcon>
          </Tooltip>
          <Tooltip label="Full Sync">
            <BasicActionIcon
              variant="light"
              color="teal"
              onClick={() => fullSync.mutate()}
              loading={fullSync.isPending}
              disabled={testConnection.isPending || fullSync.isPending}
            >
              <IconRefresh size={18} />
            </BasicActionIcon>
          </Tooltip>
          <Tooltip label="View Memberships">
            <BasicActionIcon
              variant="light"
              color="violet"
              onClick={() => setMembershipsOpen(true)}
            >
              <IconUsers size={18} />
            </BasicActionIcon>
          </Tooltip>
          <Tooltip label="View Sync Logs">
            <BasicActionIcon
              variant="light"
              color="gray"
              onClick={() => setLogsOpen(true)}
            >
              <IconListDetails size={18} />
            </BasicActionIcon>
          </Tooltip>
        </Group>
      ),
    },
    getRowId: (row) => row.id,
    totalCount: mappings.length,
    isLoading: mappingsQuery.isLoading,
    isFetching: mappingsQuery.isFetching,
    isError: mappingsQuery.isError,
  });

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [{ text: 'Settings' }, { text: 'Azure Group Sync' }],
        }}
        title="Azure AD Group Sync"
        helpText="Configure Azure AD group-to-scope mappings for automated user provisioning"
      />
      {status && (
        <Group mb="md" gap="xs">
          <Badge color={status.isConfigured ? 'green' : 'red'} size="md">
            {status.isConfigured ? 'Configured' : 'Not Configured'}
          </Badge>
          <Text size="xs" c="dimmed">
            {status.mappingCount} mapping{status.mappingCount !== 1 ? 's' : ''}{' '}
            &middot; {status.activeMembershipCount} active membership
            {status.activeMembershipCount !== 1 ? 's' : ''}
          </Text>
        </Group>
      )}
      <AzureGroupSyncMappingDialog
        open={dialogState.isOpen}
        onClose={() => setDialogState({ isOpen: false, mapping: null })}
        onSave={() => setDialogState({ isOpen: false, mapping: null })}
        mapping={dialogState.mapping}
      />
      <AzureGroupSyncMembershipsDrawer
        opened={membershipsOpen}
        onClose={() => setMembershipsOpen(false)}
      />
      <AzureGroupSyncLogsDrawer
        opened={logsOpen}
        onClose={() => setLogsOpen(false)}
      />
      {table}
    </>
  );
};
